import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

type ViewMode = 'admin' | 'staff';

interface ViewModeContextType {
  viewMode: ViewMode;
  setViewMode: (mode: ViewMode) => void;
  isAdminView: boolean;
  isStaffView: boolean;
  canToggleViewMode: boolean; // Only true for agent@botpros.ai
}

const ViewModeContext = createContext<ViewModeContextType | undefined>(undefined);

const DEVELOPER_EMAIL = 'agent@botpros.ai';
const STORAGE_KEY = 'agi-deposits-view-mode';

interface ViewModeProviderProps {
  children: ReactNode;
  userEmail?: string;
}

export const ViewModeProvider: React.FC<ViewModeProviderProps> = ({ children, userEmail }) => {
  const canToggleViewMode = userEmail === DEVELOPER_EMAIL;

  const [viewMode, setViewModeState] = useState<ViewMode>(() => {
    // Only load from storage if user can toggle
    if (typeof window !== 'undefined' && canToggleViewMode) {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored === 'admin' || stored === 'staff') {
        return stored;
      }
    }
    // Default: admin for developer, admin for everyone else (for now)
    return 'admin';
  });

  // Persist to localStorage when changed (only for developer)
  useEffect(() => {
    if (canToggleViewMode) {
      localStorage.setItem(STORAGE_KEY, viewMode);
    }
  }, [viewMode, canToggleViewMode]);

  const setViewMode = (mode: ViewMode) => {
    if (canToggleViewMode) {
      setViewModeState(mode);
    }
  };

  const value: ViewModeContextType = {
    viewMode,
    setViewMode,
    isAdminView: viewMode === 'admin',
    isStaffView: viewMode === 'staff',
    canToggleViewMode,
  };

  return (
    <ViewModeContext.Provider value={value}>
      {children}
    </ViewModeContext.Provider>
  );
};

export const useViewMode = (): ViewModeContextType => {
  const context = useContext(ViewModeContext);
  if (context === undefined) {
    throw new Error('useViewMode must be used within a ViewModeProvider');
  }
  return context;
};

export default ViewModeContext;
